import { cva, type VariantProps } from 'class-variance-authority'
import { createElement, type HTMLAttributes, type ReactNode } from 'react'
import { Button as AriaButton } from 'react-aria-components'
import { cn } from '../../lib/cn'
import { focusRing } from '../../lib/focus-ring'
import { Icon } from '../../atoms/icon/icon'
import { PillDeadline } from '../../atoms/pill-deadline/pill-deadline'

/**
 * The Title set (445:44433): Size = Large (403:43831), Medium (403:43892), Small
 * (517:10038).
 *
 * - Large is a page or drawer header: H1 over a Supporting Text subtitle in the
 *   unbound #6A7282 (`gray-500`), both on one line (the column does not wrap),
 *   then the deadline pill and the close cross. The heading column, the pill and
 *   the cross are the three children of one space-between row whose stored item
 *   spacing is 16px, the minimum gap here: with both shown, the pill floats
 *   between the heading and the cross, as the component lays it out.
 * - Medium is a card header: H3 over a Body subtitle in Light dark.
 * - Small is a sub-section header: Bold 18/18 (not a board style) over Body.
 *
 * Medium and Small take an action (the Figma draws a text-link Button,
 * "Download all files") and the arrow (the component's `Arrow` boolean, on by
 * default). The component draws arrow-up only, and so does this, open or closed.
 *
 * Keyboard focus takes the kit's accent outline, which is not drawn in the Figma.
 */
export const titleVariants = cva('flex font-atlas-sans', {
	variants: {
		size: {
			large: 'items-center justify-between gap-4',
			medium: 'items-start gap-6',
			small: 'items-start gap-6',
		},
	},
	defaultVariants: { size: 'large' },
})

const columnClass = {
	large: 'flex min-w-0 flex-col gap-0.5 whitespace-nowrap',
	medium: 'flex min-w-0 flex-1 flex-col gap-2',
	small: 'flex min-w-0 flex-1 flex-col justify-center gap-1',
}
const headingClass = {
	large: 'text-atlas-h1 text-atlas-dark',
	medium: 'text-atlas-h3 text-atlas-dark',
	small: 'text-atlas-title-small text-atlas-dark',
}
const subtitleClass = {
	large: 'text-atlas-supporting text-atlas-gray-500',
	medium: 'text-atlas-body text-atlas-light-dark',
	small: 'text-atlas-body text-atlas-light-dark',
}
const defaultLevel = { large: 1, medium: 2, small: 3 } as const

const iconButtonClass = cn('inline-flex shrink-0 cursor-pointer rounded-atlas-sm', focusRing)

export interface TitleProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'>, VariantProps<typeof titleVariants> {
	title: ReactNode
	subtitle?: ReactNode
	/** The heading element's level; defaults to 1 for Large, 2 for Medium, 3 for Small. */
	headingLevel?: 1 | 2 | 3 | 4 | 5 | 6
	/** Large only: the formatted deadline shown in a Pill Deadline. */
	deadline?: string
	/** Large only: shows the close cross and is called when it is pressed. */
	onClose?: () => void
	closeLabel?: string
	/** Medium and Small: a control beside the heading, such as a text-link Button. */
	action?: ReactNode
	/** Medium and Small: shows the arrow, as the component's `Arrow` boolean (on by default). */
	arrow?: boolean
	/** Medium and Small: whether the section is open, announced on the arrow; defaults to open. */
	isExpanded?: boolean
	/** Called with the new state when the arrow is pressed; without it the arrow is decorative. */
	onExpandedChange?: (isExpanded: boolean) => void
	/** Accessible name of the collapse arrow; defaults to the title when it is text. */
	toggleLabel?: string
}

/**
 * A heading with its subtitle and the header controls the Figma draws for its
 * size. With `onExpandedChange` the arrow is a disclosure button whose state is
 * `aria-expanded`; the glyph stays arrow-up either way, as the Title component and
 * the collapsed Task details card (463:50186) draw it.
 */
export function Title(props: TitleProps) {
	const {
		size,
		title,
		subtitle,
		headingLevel,
		deadline,
		onClose,
		closeLabel = 'Close',
		action,
		arrow = true,
		isExpanded = true,
		onExpandedChange,
		toggleLabel,
		className,
		...rest
	} = props
	const s = size ?? 'large'
	const showArrow = s !== 'large' && arrow
	return (
		<div {...rest} className={cn(titleVariants({ size: s }), className)}>
			<div className={columnClass[s]}>
				{createElement(`h${headingLevel ?? defaultLevel[s]}`, { className: headingClass[s] }, title)}
				{subtitle ? <p className={subtitleClass[s]}>{subtitle}</p> : null}
			</div>
			{s === 'large' && deadline ? <PillDeadline className="shrink-0" label={deadline} /> : null}
			{s === 'large' && onClose ? (
				<AriaButton aria-label={closeLabel} onPress={onClose} className={iconButtonClass}>
					<Icon name="x" />
				</AriaButton>
			) : null}
			{s !== 'large' ? action : null}
			{showArrow && onExpandedChange ? (
				<AriaButton
					aria-label={toggleLabel ?? (typeof title === 'string' ? title : 'Toggle section')}
					aria-expanded={isExpanded}
					onPress={() => onExpandedChange(!isExpanded)}
					className={iconButtonClass}
				>
					<Icon name="arrow-up" />
				</AriaButton>
			) : null}
			{showArrow && !onExpandedChange ? <Icon name="arrow-up" /> : null}
		</div>
	)
}
